import { lazy, Suspense, useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient, ApiError } from "../api/apiClient";
import { getStrings, hasStrings, homeShellString } from "../i18n/strings";
import { useLanguages } from "../languages/useLanguages";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { HomeNetworkPanel, HomePanel, HomeReportCard } from "../components/HomeReportCard";
import { HomeVisitorIpCard } from "../components/HomeVisitorIpCard";
import { HomeIpSearch } from "../components/HomeIpSearch";
import { HomeMoreTools } from "../components/HomeMoreTools";
import { HomeDeveloperTools } from "../components/HomeDeveloperTools";
import { HomeHero } from "../components/HomeHero";
import { HomeDetects } from "../components/HomeDetects";
import { HomeIndexSection } from "../components/HomeIndexExplainer";
import { HomeUseCases } from "../components/HomeUseCases";
import { HomeFaq } from "../components/HomeFaq";
import { DeferredLocationMap } from "../components/DeferredLocationMap";
import { addressesEqual, isIpv4Address, isIpv6Address } from "../utils/ipAddress";
import { homePath, localizedPath } from "../utils/localizedPath";
import { trackEvent } from "../utils/telemetry";
import { useDeveloperSession } from "../developer/auth/useDeveloperSession";
import type { PublicIpRiskResultDto } from "../types/dto";

// The share dialog and its export/environment-check helpers are only needed after a deliberate
// click, so they stay out of the initial home bundle.
const ShareCardModal = lazy(() => import("../components/ShareCardModal").then((module) => ({ default: module.ShareCardModal })));

/** anonymous-quota-v2: the website's per-visitor daily allowance refusal (IpRiskErrorCodes.AnonymousDailyLimitExceeded). */
function isDailyLimitError(err: unknown): boolean {
  return err instanceof ApiError && err.code === "ANONYMOUS_DAILY_LIMIT_EXCEEDED";
}

// spec §二十一: `?ip=` is the shortcut for looking up an address directly (the old bare `/{ipv4}` path
// route now redirects here — see HomeRoute in App.tsx). It is the same page in every other
// respect — the address simply arrives from the URL instead of from the box, and the query starts
// without a click.
export function HomePage() {
  const { lang: langParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // ?ip= is the page's lookup target whenever it is present: the Check button writes it, Back/Forward
  // and the language switcher carry it, and the effect further down is the only thing that turns it
  // into a query. An empty ?ip= counts as absent (auto-detect).
  const ipQueryParam = searchParams.get("ip")?.trim() || undefined;
  // The old bare /{ipv4} path route (still IPv4-only by design, see ipAddress.ts) redirects here, but
  // ?ip= itself is a general-purpose deep link and must accept whatever address the backend's own
  // IpAddressValidator.EnsureValid does — IPv6 included. A ?ip= value the backend would reject anyway
  // (isIpv6Address is a syntax check, not a policy one) simply falls through to the normal server error.
  const urlIpAddress = ipQueryParam && isLookupAddress(ipQueryParam) ? ipQueryParam : undefined;
  // `?ip=` is an independent query parameter now, not a value borrowed from the :lang path slot (the old
  // bare /{ipv4} route redirects to /?ip={ipv4} in HomeRoute before HomePage ever renders, so
  // langParam can never be an IP address here) — so the path's language prefix, when present, is always
  // trustworthy and must be honored even when a lookup address also arrived via ?ip=. A visitor on
  // /zh-cn/index.html?ip=8.8.8.8 gets the Chinese UI looking up 8.8.8.8, not an English page.
  const lang = langParam ?? "en";
  const strings = getStrings(lang);
  const [ipInput, setIpInput] = useState(ipQueryParam ?? "");
  // Treated as edited from the start when the address came from the URL: everything downstream that keys
  // off this flag ("detected automatically", the auto-detect status line, the WebRTC/DNS tiles that only
  // describe the visitor's OWN connection) must behave exactly as it does for a typed-in address. A
  // visitor who opens /?ip=8.8.8.8 is looking up someone else's address, not their own. An invalid
  // ?ip=abc counts too: it pre-fills the box so the inline error explains it, and the visitor's own
  // address is never passed off as the answer to the URL's question.
  const [ipEdited, setIpEdited] = useState(Boolean(ipQueryParam));
  const [result, setResult] = useState<PublicIpRiskResultDto | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // anonymous-quota-v2: set when the lookup was refused for the visitor's spent DAILY allowance, so the banner
  // can offer the way past it (a free API key / plans) instead of reading as an ordinary failed query.
  const [dailyLimitReached, setDailyLimitReached] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const t = <K extends Parameters<typeof homeShellString>[1]>(key: K) => homeShellString(strings, key);

  // Inline, client-side-only syntax check for the manual search box — never a substitute for the
  // backend's own IpAddressValidator.EnsureValid (still the final authority; a syntactically valid
  // address can still be rejected server-side). Only flags non-empty input the visitor has actually
  // edited that is neither a valid IPv4 nor IPv6 literal, so it never nags at an empty box or at an
  // address that arrived pre-filled and valid from ?ip=/auto-detect.
  const ipInputError = ipInput.trim() && ipEdited && !isLookupAddress(ipInput.trim()) ? t("homeInvalidIpInline") : null;

  const { data: languages } = useLanguages();
  const defaultUrlCode = languages?.find((language) => language.isDefault)?.urlCode ?? "en";
  const { data: developerSession } = useDeveloperSession();
  const toolPath = useCallback(
    (path: string) => localizedPath(lang, defaultUrlCode, path),
    [lang, defaultUrlCode],
  );

  useEffect(() => {
    // <html lang> must be the Languages table's BCP 47 Code (e.g. "zh-CN"), not the lowercase URL
    // segment (spec §十一) — falls back to the raw param only until the language list has loaded.
    document.documentElement.lang = languages?.find((language) => language.urlCode === lang)?.code ?? lang;
    // Kept identical to web/index.html's build-time snapshot (see scripts/homeSeoSnapshot.ts) so a
    // crawler that renders JS never sees a different title than one that didn't.
    const seoTitle = strings.homeSeoTitle ?? `${strings.heroTitle} — ${strings.brand}`;
    const seoDescription = strings.homeSeoDescription ?? strings.heroSubtitle;
    document.title = seoTitle;

    setMeta("description", seoDescription);
    setMeta("keywords", strings.seoKeywords ?? [
      strings.heroTitle,
      strings.fieldRiskScore,
      strings.fieldRiskLevel,
      strings.fieldProxy,
      strings.fieldVpn,
      strings.fieldTor,
      strings.fieldAsn,
      strings.fieldIsp,
      strings.sectionGeo,
    ].join(", "));
    setMeta("robots", "index, follow, max-image-preview:large");
    setMeta("application-name", strings.brand);
    setProperty("og:type", "website");
    setProperty("og:site_name", strings.brand);
    setProperty("og:title", seoTitle);
    setProperty("og:description", seoDescription);
    // og:locale uses Open Graph's underscore form (zh_CN), never hreflang's hyphenated BCP 47 form
    // (zh-CN) — the two must not be mixed (spec §十五).
    const currentLanguageCode = languages?.find((language) => language.urlCode === lang)?.code ?? lang;
    setProperty("og:locale", ogLocale(currentLanguageCode));
    setMeta("twitter:card", "summary");
    setMeta("twitter:title", seoTitle);
    setMeta("twitter:description", seoDescription);

    // The default language's home page carries no prefix and is canonical at the origin root.
    // Derived from the language list rather than assuming "en" so this cannot drift away from the
    // sitemap and the Worker's redirects, which both read the same isDefault flag.
    const canonicalPath = homePath(lang, defaultUrlCode);
    setCanonicalUrl(canonicalPath);
    setProperty("og:url", `${window.location.origin}${canonicalPath}`);
    setJsonLd({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: strings.brand,
      url: `${window.location.origin}${canonicalPath}`,
      description: seoDescription,
      applicationCategory: "SecurityApplication",
      operatingSystem: "Any",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      featureList: [
        strings.fieldRiskScore,
        strings.fieldRiskLevel,
        strings.fieldProxy,
        strings.fieldVpn,
        strings.fieldTor,
        strings.fieldAsn,
        strings.sectionGeo,
      ],
    });
  }, [lang, strings, defaultUrlCode, languages]);

  // Cross-links every translated language variant of the homepage so crawlers can discover them
  // directly — the header's language switcher only reaches them via JS navigation.
  useEffect(() => {
    if (!languages) return;

    document.querySelectorAll('link[rel="alternate"][data-home-alt]').forEach((el) => el.remove());
    document.querySelectorAll('meta[property="og:locale:alternate"][data-home-alt-locale]').forEach((el) => el.remove());

    const translated = languages.filter((language) => hasStrings(language.urlCode));
    for (const language of translated) {
      const path = homePath(language.urlCode, defaultUrlCode);
      // hreflang is BCP 47 (language.code, e.g. "zh-CN") — distinct from the lowercase route
      // segment (language.urlCode, e.g. "zh-cn") used to build the URL itself (spec §九).
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = language.code;
      link.href = `${window.location.origin}${path}`;
      link.dataset.homeAlt = "true";
      document.head.appendChild(link);

      if (language.urlCode !== lang) {
        const alternate = document.createElement("meta");
        alternate.setAttribute("property", "og:locale:alternate");
        alternate.setAttribute("content", ogLocale(language.code));
        alternate.dataset.homeAltLocale = "true";
        document.head.appendChild(alternate);
      }
    }
    if (translated.some((language) => language.urlCode === defaultUrlCode)) {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = "x-default";
      link.href = `${window.location.origin}${homePath(defaultUrlCode, defaultUrlCode)}`;
      link.dataset.homeAlt = "true";
      document.head.appendChild(link);
    }
  }, [languages, lang, defaultUrlCode]);

  // Silent best-effort lookup of the visitor's own public IP, run once on load — never surfaces its
  // own errors. Its result both pre-fills the box and shows the full card immediately, so the page
  // lands with data already on screen instead of waiting for a click.
  const myIpQuery = useQuery({
    queryKey: ["my-ip"],
    queryFn: () => apiClient.post<PublicIpRiskResultDto>("/api/public/ip/query", {}),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const autoShownRef = useRef(false);

  // The ?ip= value (raw, valid or not) the URL effect below last acted on. Keyed per value rather than
  // per mount: a search-param-only navigation (Back/Forward, /zh-cn/ → /ja/, a Check) does not remount
  // this page, so "handle the URL once" would silently ignore every later ?ip= change.
  const handledIpParamRef = useRef<string | undefined>(undefined);
  // The address whose response is allowed to land on screen. Without it a slow response for a previous
  // target (8.8.8.8, then Back to 1.1.1.1) could overwrite the result for the address the URL now names.
  const lookupTargetRef = useRef<string | null>(null);

  const query = useMutation({
    mutationFn: (ipAddress: string) =>
      apiClient.post<PublicIpRiskResultDto>("/api/public/ip/query", { ipAddress: ipAddress || undefined }),
    // Covers both the manual search box submit and a ?ip= deep link — both are genuine lookup intent.
    // The silent auto-detect (myIpQuery) never goes through this mutation, so it never fires these.
    onMutate: () => {
      trackEvent({ eventType: "LookupStarted", pagePath: window.location.pathname });
    },
    onSuccess: (data, ipAddress) => {
      trackEvent({ eventType: "LookupSucceeded", pagePath: window.location.pathname });
      if (ipAddress !== lookupTargetRef.current) return;
      setResult(data);
      setErrorMessage(null);
      setDailyLimitReached(false);
    },
    onError: (err, ipAddress) => {
      trackEvent({ eventType: "LookupFailed", pagePath: window.location.pathname });
      if (ipAddress !== lookupTargetRef.current) return;
      setResult(null);
      const limited = isDailyLimitError(err);
      setDailyLimitReached(limited);
      setErrorMessage(limited ? t("homeDailyLimitReached") : err instanceof ApiError ? err.message : strings.errorGeneric);
    },
  });

  const runQuery = query.mutate;
  const lookup = useCallback((ipAddress: string) => {
    lookupTargetRef.current = ipAddress;
    runQuery(ipAddress);
  }, [runQuery]);

  // spec §二十一: the URL is the single source of the lookup target. Every distinct ?ip= value runs the
  // ordinary query exactly once — on load, after a Check (which only writes the URL), on Back/Forward —
  // and nothing else queries it again for the same value. Marking autoShownRef is what stops the
  // visitor's own auto-detected address from racing in and replacing the result they navigated for —
  // myIpQuery still runs (isCurrentIp needs it), it just no longer owns the card.
  useEffect(() => {
    if (ipQueryParam === handledIpParamRef.current) return;
    const previousIpParam = handledIpParamRef.current;
    handledIpParamRef.current = ipQueryParam;

    if (urlIpAddress) {
      autoShownRef.current = true;
      setIpEdited(true);
      setIpInput(urlIpAddress);
      lookup(urlIpAddress);
    } else if (ipQueryParam) {
      // ?ip=abc: no query for it and no visitor result standing in for it — the pre-filled box and its
      // inline error are the whole answer. No redirect either, so there is nothing to loop on.
      autoShownRef.current = true;
      lookupTargetRef.current = null;
      setIpEdited(true);
      setIpInput(ipQueryParam);
      setResult(null);
      setErrorMessage(null);
    } else if (previousIpParam !== undefined) {
      // ?ip= went away (Back to the plain home page): back to the auto-detect state. The auto-show
      // effect below re-shows the cached myIpQuery result — no new request.
      autoShownRef.current = false;
      lookupTargetRef.current = null;
      setIpEdited(false);
      setIpInput("");
      setResult(null);
      setErrorMessage(null);
    }
  }, [ipQueryParam, urlIpAddress, lookup]);

  useEffect(() => {
    if (autoShownRef.current || ipEdited || !myIpQuery.data) {
      return;
    }
    autoShownRef.current = true;
    setIpInput(myIpQuery.data.meta.ipAddress);
    setResult(myIpQuery.data);
  }, [myIpQuery.data, ipEdited]);

  // WebRTC/DNS/IPv6/AI-access describe *this browser's* network path, not whatever address is
  // currently displayed — true only when the result on screen is the visitor's own auto-detected IP,
  // never for a manually looked-up address (spec: querying 8.8.8.8 must not show the visitor's own
  // WebRTC/DNS results as if they belonged to 8.8.8.8). Compared with the same IPv4/IPv6-aware
  // normalizer webrtcLeak.ts already uses, not a raw string match, so e.g. a compressed vs. expanded
  // spelling of the same IPv6 address doesn't read as "different".
  const isCurrentIp = Boolean(
    result && myIpQuery.data && addressesEqual(result.meta.ipAddress, myIpQuery.data.meta.ipAddress),
  );

  // Populated lazily the first time the share modal opens (see ShareIcon button below) — the share
  // card upgrades from the plain IP-only image to one with verified WebRTC/DNS/IPv6 status once this
  // resolves, rather than blocking the click on it.
  const [envReportId, setEnvReportId] = useState<string | null>(null);
  const envCheckStartedRef = useRef(false);

  // Every successful query (auto-detect or manual) produces a new shareId, including a repeat query
  // of the same address — keying the reset off it means a share verification run for a previous
  // result (of this IP or any other) can never be reused for whatever is now on screen.
  useEffect(() => {
    setEnvReportId(null);
    envCheckStartedRef.current = false;
  }, [result?.shareId]);

  // A Check only writes ?ip= — the URL effect above runs the query, so one click is one lookup and the
  // address survives a reload, a shared link and a language switch. Other query parameters (UTM tags)
  // are kept. The one case the URL cannot drive is re-checking the address it already names, which
  // changes nothing in the URL, so that click queries directly.
  function submit(event: FormEvent) {
    event.preventDefault();
    if (ipInputError) return;
    const target = ipInput.trim();
    if (!target) {
      if (ipQueryParam) {
        // An emptied box means "my own connection" — drop ?ip= and let the URL effect fall back to the
        // auto-detected result, rather than showing the visitor's IP under a URL naming another one.
        const params = new URLSearchParams(searchParams);
        params.delete("ip");
        setSearchParams(params);
      } else {
        lookup("");
      }
      return;
    }
    if (!isLookupAddress(target)) return;
    if (target === ipQueryParam) {
      lookup(target);
      return;
    }
    const params = new URLSearchParams(searchParams);
    params.set("ip", target);
    setSearchParams(params);
  }

  // The card's "re-check" button. Goes through refetch()'s returned result rather than leaving it to
  // the auto-show effect above: that effect deliberately fires only once (autoShownRef), so a plain
  // refetch() would update the query cache while the card on screen kept showing the previous
  // result. Does not set ipEdited — this is still the visitor's own auto-detected address.
  async function refreshMyIp() {
    const refreshed = await myIpQuery.refetch();
    if (refreshed.data) {
      setResult(refreshed.data);
      setIpInput(refreshed.data.meta.ipAddress);
      setErrorMessage(null);
    }
  }

  function openShareModal() {
    setShareModalOpen(true);
    trackEvent({ eventType: "ShareResult", pagePath: window.location.pathname });
    // Only verified for the visitor's own IP (see isCurrentIp) — running this against a manually
    // looked-up address would submit the visitor's own WebRTC/DNS/IPv6 evidence to a report about
    // someone else's server. envReportId stays null for any other result, and ShareCardModal
    // already falls back to the plain card in that case.
    if (isCurrentIp && !envCheckStartedRef.current) {
      envCheckStartedRef.current = true;
      void import("../utils/shareEnvironmentCheck")
        .then(({ runShareEnvironmentCheck }) => runShareEnvironmentCheck())
        .then((report) => {
          if (report) setEnvReportId(report.id);
        })
        .catch(() => {
          // The basic share card remains usable when this optional background check fails.
        });
    }
  }

  const isChecking = query.isPending || (myIpQuery.isLoading && !ipEdited);
  // The silent auto-detect query (myIpQuery) has no onError handler of its own — unlike the manual
  // "Check" mutation, it's never supposed to bother the visitor with an error banner for a query they
  // didn't ask for. But that silence previously meant a failed auto-detect still finished with
  // isChecking=false and no result, which fell into the "detected automatically" success branch below:
  // a green checkmark claiming success while the buttons next to it stayed permanently disabled with
  // no explanation. This is the one case statusText now has to tell apart from a genuine success.
  const autoDetectFailed = !ipEdited && !result && myIpQuery.isError;
  // Being throttled is the one auto-detect failure the visitor can actually do something about, and
  // it's the likeliest one on a phone: the limiter partitions by public IP, and a mobile carrier's
  // CGNAT puts many subscribers behind the same one, so a visitor can be over the limit without
  // having done anything unusual themselves. Telling them "we couldn't detect your IP" there sends
  // them hunting for a problem with their own network instead of just waiting a few seconds.
  const autoDetectRateLimited = autoDetectFailed && myIpQuery.error instanceof ApiError && myIpQuery.error.status === 429;
  // The daily allowance answers 429 too, but "wait a moment" is wrong advice for it — only a new day or a
  // key gets the visitor past it.
  const failedStatusText = autoDetectFailed && isDailyLimitError(myIpQuery.error)
    ? t("homeDailyLimitReached")
    : autoDetectRateLimited ? t("homeAutoDetectRateLimited") : t("homeAutoDetectFailed");
  // The place name is the location panel's content; the map only illustrates it, so a result with a
  // place but no coordinates still gets the panel (the map area then says so instead of vanishing).
  const homePlace = result
    ? [result.location.cityName, result.location.regionName, result.location.countryName].filter(Boolean).join(", ")
    : "";

  return (
    <>
      {/* variant="home" narrows the header's rail to this page's content column so the logo lines up
          with the hero copy and the card below it (spec §二十). No other page is affected. */}
      <SiteHeader variant="home" />
      <main className="home-page">
        {/* The dark hero is a real full-bleed page section now, not a content-column child faking
            width with negative margins (spec §三): the <section> spans the viewport and
            .home-hero-shell__inner is what re-establishes the 1060px column inside it. The backdrop
            is pure CSS (see index.css), so this adds no content of its own and needs no counterpart
            in scripts/homeSeoSnapshot.ts. */}
        <section className="home-hero-shell">
          <div className="home-hero-shell__inner">
            <HomeHero strings={strings} />

            {errorMessage && (
              <div className="error-banner">
                {errorMessage}
                {dailyLimitReached && (
                  <>
                    {" "}
                    <a href={toolPath("/developer/register")}>{t("homeDailyLimitSignup")}</a>
                    {" · "}
                    <a href={toolPath("/pricing.html")}>{t("homeDailyLimitPricing")}</a>
                  </>
                )}
              </div>
            )}

            {/* The second action — checking someone else's address — stays inside the dark hero,
                directly above the visitor's own card and at exactly the card's width (spec §十五).
                Keeps the #ip-check anchor other pages link to and the same submit/mutate wiring. */}
            <HomeIpSearch
              strings={strings}
              value={ipInput}
              onChange={(value) => {
                setIpEdited(true);
                setIpInput(value);
              }}
              onSubmit={submit}
              checking={isChecking}
              error={ipInputError}
            />

            {/* A failed auto-detect has no result to show a card for — this stands alone where the
                card would otherwise be, with the one thing the visitor can do about it (retry).
                Everything else about the visitor's own IP (the card, its "detected automatically"
                line) only ever appears once there is a result — see HomeVisitorIpCard. */}
            {autoDetectFailed && (
              <p className="home-detect__status home-detect__status--failed">
                <svg className="home-detect__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v5M12 16.5h.01" />
                </svg>
                {failedStatusText}
                {/* Without this the only way back from a failed auto-detect is a full page reload,
                    which is exactly the reflex that keeps a throttled visitor throttled. */}
                <button type="button" className="home-detect__retry" onClick={() => myIpQuery.refetch()} disabled={myIpQuery.isFetching}>
                  {myIpQuery.isFetching ? strings.loading : t("homeRetryLabel")}
                </button>
              </p>
            )}

            {/* The screenshot block: address, auto-detect status, network identity and the
                NetRiskScan Index ring — the first thing a visitor sees, before any interaction. */}
            {result ? (
              <HomeVisitorIpCard
                result={result}
                strings={strings}
                helpPath={toolPath("/disclaimer.html")}
                showAutoDetected={!ipEdited}
                onRefresh={refreshMyIp}
                refreshing={myIpQuery.isFetching}
                onShare={openShareModal}
              />
            ) : isChecking ? (
              <HomeCardSkeleton />
            ) : null}
          </div>
        </section>

        {/* Everything below the hero shares one content column with the hero's inner rail — see
            --content-width in index.css, which the site header and every other page share too. No
            section sets a width of its own any more. */}
        <div className="home-page__content">
          {result && shareModalOpen && (
            <Suspense fallback={null}>
              <ShareCardModal
                open
                onClose={() => setShareModalOpen(false)}
                result={result}
                strings={strings}
                lang={lang}
              // The home page no longer runs the browser-side WebRTC/DNS/IPv6 probes — that report
              // lives on /environment-check.html, which is linked under "more tools" below. Null here
              // means the Markdown/BBCode/plain-text exports omit the Environment lines entirely
              // rather than publishing four "couldn't check" rows for probes nothing started.
                checks={null}
                envReportId={envReportId}
              />
            </Suspense>
          )}

          {result && <HomeReportCard result={result} strings={strings} />}

          {result && <HomeNetworkPanel result={result} strings={strings} />}

          {/* High-intent moment: the visitor just saw a real result, so this is where "I could use this
              in my own app" naturally occurs to them. Deliberately tiny — one line, one link, no pricing
              card — the full CLI/JS/Python/.NET section further down (HomeDeveloperTools) is where that
              intent gets satisfied. Never shown before a result exists; this is not a landing-page CTA. */}
          {result && (
            <p className="home-inline-dev-cta">
              {t("homeInlineDevCtaLead")}{" "}
              <a
                href={developerSession?.authenticated ? toolPath("/developer") : toolPath("/pricing.html")}
                onClick={() => trackEvent({ eventType: "DeveloperCtaClicked", pagePath: window.location.pathname })}
              >
                {t("homeInlineDevCtaLink")} <span aria-hidden="true">→</span>
              </a>
            </p>
          )}

          {/* Kept last by design: where an address is registered is the least
              actionable thing on the page, and it must never push the score below the fold. The
              place name carries most of the value here — the map is only its illustration. */}
          {result && (homePlace || result.location.latitude !== null || result.location.longitude !== null) && (
            <HomePanel icon="map" title={t("homeSectionMap")}>
              {homePlace && <p className="home-place">{homePlace}</p>}
              <div className="home-map">
                <DeferredLocationMap
                  latitude={result.location.latitude}
                  longitude={result.location.longitude}
                  label={[result.location.cityName, result.location.countryName].filter(Boolean).join(", ") || result.meta.ipAddress}
                  strings={strings}
                />
              </div>
              <p className="home-panel__note">{strings.geoDisclaimer}</p>
            </HomePanel>
          )}

          {/* "What NetRiskScan Detects" — six short capability cards. Sits below the result panels so
              it never competes with the primary check flow, and above the developer tools so a
              visitor reads what the product covers before how to integrate it. */}
          <HomeDetects strings={strings} />

          {/* Secondary conversion path for developer visitors — CLI, JS/TS SDK, and a link into the
              Developer API. Renders whether or not a check has run, but sits below the IP result and
              above "More checks" so it never competes with the primary check flow.
              apiPath is intentionally NOT /developer/login for a logged-out visitor: this CTA is
              acquisition, not the account entry point (that's SiteHeader's job, which already goes
              straight to sign-in) — sending an anonymous visitor into a login form here is exactly the
              dead end this fixes. Logged-in developers still go straight to their dashboard. */}
          <HomeDeveloperTools
            strings={strings}
            apiPath={developerSession?.authenticated ? toolPath("/developer") : toolPath("/pricing.html")}
          />

          <HomeIndexSection strings={strings} learnMoreHref={toolPath("/disclaimer.html")} />

          {/* All 9 tools, directly linked from the home page — the SEO entry-point matrix (spec §四). */}
          <HomeMoreTools strings={strings} lang={lang} defaultUrlCode={defaultUrlCode} />

          <HomeUseCases strings={strings} />

          <HomeFaq lang={lang} />
        </div>
      </main>
      <SiteFooter />
    </>
  );
}

// Block-for-block the shape of the real card — status line, address, identity, ring, verdict strip,
// footer — laid out in the same grid at the same sizes, so the hero does not grow by a couple of
// hundred pixels the moment myIpQuery lands (spec §二十七: reserve the final height, keep CLS at 0).
function HomeCardSkeleton() {
  return (
    <div className="home-visitor-card home-visitor-card--skeleton" aria-hidden="true">
      <div className="skeleton-block skeleton-block--status" />
      <div className="home-visitor-card__grid">
        <div className="home-visitor-card__info">
          <div className="skeleton-block skeleton-block--ip" />
          <div className="skeleton-block skeleton-block--identity" />
          <div className="skeleton-block skeleton-block--place" />
        </div>
        <div className="home-visitor-card__score">
          <div className="skeleton-block skeleton-block--ring" />
        </div>
      </div>
      <div className="skeleton-block skeleton-block--verdict" />
      <div className="skeleton-block skeleton-block--foot" />
    </div>
  );
}

// What this page will send as a lookup target, from the box or from ?ip= — the same two syntax checks
// the inline error uses; the backend's IpAddressValidator.EnsureValid stays the final authority.
function isLookupAddress(value: string): boolean {
  return isIpv4Address(value) || isIpv6Address(value);
}

// BCP 47 uses hyphens (zh-CN); Open Graph's og:locale/og:locale:alternate use the older
// language_TERRITORY underscore form (zh_CN) — mirrors scripts/homeSeoSnapshot.ts's build-time
// equivalent (kept separate rather than shared: that module runs under a Node-only tsconfig with no
// DOM lib, this one is browser-only).
function ogLocale(code: string): string {
  return code.replace(/-/g, "_");
}

function setCanonicalUrl(path: string) {
  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }
  canonical.href = `${window.location.origin}${path}`;
}

function setMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setJsonLd(value: Record<string, unknown>) {
  let el = document.querySelector<HTMLScriptElement>('script[data-home-structured-data]');
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.homeStructuredData = "true";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(value);
}
